<template>
  <div class="racquet-sports-container">
    <div
      class="match"
      v-for="match in matches"
      :key="match.id"
    >
      <div class="status" :class="getStatusClass(match.status)">
        {{ match.status }}
      </div>

      <div
        class="player-row"
        v-for="(participant, playerIndex) in match.participants"
        :key="playerIndex"
        :class="{ 'winner': match.result?.winner === playerIndex }"
      >
        <div class="player-name">
          <div
            v-for="(name, nameIndex) in getParticipantNames(participant)"
            :key="nameIndex"
            class="name-line"
          >
            {{ name }}
          </div>
        </div>
        <div class="scores">
          <span class="sets">{{ participant.totalSetsWon || 0 }}</span>
          <div class="set-scores">
            <span
              v-for="(score, setIndex) in participant.setScores"
              :key="setIndex"
              class="set-score"
              :class="getSetWinnerClass(participant, match.participants[playerIndex === 0 ? 1 : 0], setIndex)"
            >
              {{ formatSetScore(participant, match.participants[playerIndex === 0 ? 1 : 0], setIndex) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { timeOutline, trophyOutline, checkmarkCircleOutline } from 'ionicons/icons';
import moment from 'moment';

export default defineComponent({
  name: 'RacquetSportsResults',
  props: {
    matches: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  setup() {
    return {
      timeOutline,
      trophyOutline,
      checkmarkCircleOutline
    };
  },
  methods: {
    getParticipantNames(participant) {
      console.log('participant', participant);
      if (participant.member1 && participant.member1.name1) {
        return [participant.member1.name1];
      }

      if (participant.name1 && participant.name2) {
        return [participant.name1, participant.name2];
      }

      if (participant.member1 && participant.member1.profile && participant.member2 && participant.member2.profile) {
        return [`${participant.member1.profile.firstName} ${participant.member1.profile.lastName}`, `${participant.member2.profile.firstName} ${participant.member2.profile.lastName}`];
      }


      if (participant.member) {
        if (participant.member.profile.firstName && participant.member.profile.lastName) {
          return [`${participant.member.profile.firstName} ${participant.member.profile.lastName}`];
        }
        return [participant.member.name || participant.member.name1 || 'Unknown Member'];
      }

      if (participant.members && participant.members.length > 0) {
        return participant.members.map(member => {
          if (member.profile && member.profile.firstName && member.profile.lastName) {
            return `${member.profile.firstName} ${member.profile.lastName}`;
          }
          return member.name || member.name1 || 'Unknown';
        });
      }

      if (participant.name1) {
        return [participant.name1];
      }

      return [participant.name || 'Unknown'];
    },

    getParticipantName(participant) {
      // Keep this method for backwards compatibility in result text
      const names = this.getParticipantNames(participant);
      return names.join(' / ');
    },

    getSetHeaders(match) {
      if (!match.participants || !match.participants[0]?.setScores) return [];
      return match.participants[0].setScores;
    },

    getStatusClass(status) {
      const statusLower = (status || '').toLowerCase();
      if (statusLower.includes('completed') || statusLower.includes('finished')) return 'completed';
      if (statusLower.includes('progress') || statusLower.includes('live')) return 'live';
      if (statusLower.includes('not started') || statusLower.includes('scheduled')) return 'scheduled';
      return 'default';
    },

    getSetWinnerClass(participant, opponent, setIndex) {
      const participantScore = parseInt(participant.setScores?.[setIndex]) || 0;
      const opponentScore = parseInt(opponent.setScores?.[setIndex]) || 0;
      if (participantScore > opponentScore) return 'set-winner';
      if (participantScore < opponentScore) return 'set-loser';
      return '';
    },

    getResultText(match) {
      if (match.result?.score) return `Final Score: ${match.result.score}`;
      if (match.result?.sets) return `Sets Won: ${match.result.sets}`;
      const winner = match.participants?.[match.result?.winner];
      if (winner) return `Winner: ${this.getParticipantName(winner)}`;
      return 'Match Completed';
    },

    formatDateTime(timestamp) {
      return moment(parseInt(timestamp)).format('MMM DD, YYYY HH:mm');
    },

    formatSetScore(participant, opponent, setIndex) {
      const participantScore = participant.setScores?.[setIndex];
      const opponentScore = opponent.setScores?.[setIndex];

      if (!participantScore && participantScore !== 0) return '-';

      // Check if there's a tiebreak score for this set
      const participantTiebreak = participant.tiebreakScores?.[setIndex];
      const opponentTiebreak = opponent.tiebreakScores?.[setIndex];

      if (participantTiebreak !== null && participantTiebreak !== undefined &&
          opponentTiebreak !== null && opponentTiebreak !== undefined) {
        // Show tiebreak score in parentheses
        return `${participantScore}(${participantTiebreak})`;
      }

      return participantScore.toString();
    }
  }
});
</script>

<style lang="scss" scoped>
// Dark Theme Variables
$background: #0f1419;
$card-bg: linear-gradient(135deg, rgba(20, 20, 22, 0.6) 0%, rgba(15, 15, 17, 0.4) 100%);
$text-primary: rgba(255, 255, 255, 0.95);
$text-secondary: rgba(255, 255, 255, 0.7);
$text-light: rgba(255, 255, 255, 0.5);
$accent-color: #4caf50;
$border-color: rgba(255, 255, 255, 0.1);

.racquet-sports-container {
  padding: 1rem;
  background: transparent;
  border-radius: 16px;
}

.match {
  margin-bottom: 1rem;
  padding: 1rem;
  background: $card-bg;
  backdrop-filter: blur(30px) saturate(180%);
  -webkit-backdrop-filter: blur(30px) saturate(180%);
  border-radius: 12px;
  border: 1px solid $border-color;
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.status {
  font-size: 0.7rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  margin-bottom: 0.75rem;
  display: inline-block;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;

  &.completed {
    background: rgba(16, 185, 129, 0.2);
    color: #10b981;
    border: 1px solid rgba(16, 185, 129, 0.3);
  }

  &.live {
    background: rgba(249, 115, 22, 0.2);
    color: #f97316;
    border: 1px solid rgba(249, 115, 22, 0.3);
  }

  &.scheduled {
    background: rgba(255, 255, 255, 0.1);
    color: $text-secondary;
    border: 1px solid $border-color;
  }

  &.default {
    background: rgba(255, 255, 255, 0.1);
    color: $text-secondary;
    border: 1px solid $border-color;
  }
}

.player-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-radius: 6px;
  transition: background-color 0.15s ease-in-out;

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  &.winner {
    background: rgba($accent-color, 0.05);

    .player-name {
      font-weight: 600;
      color: $accent-color;
    }

    .sets {
      color: $accent-color;
    }
  }
}

.player-name {
  font-size: 0.875rem;
  color: $text-primary;
  font-weight: 500;
  transition: color 0.15s ease-in-out;
}

.name-line {
  line-height: 1.2;

  &:not(:last-child) {
    margin-bottom: 0.125rem;
  }
}

.scores {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.sets {
  font-size: 1.125rem;
  font-weight: 700;
  color: $text-primary;
  min-width: 20px;
  text-align: center;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 0.25rem 0.5rem;
}

.set-scores {
  display: flex;
  gap: 0.375rem;
}

.set-score {
  font-size: 0.875rem;
  color: $text-secondary;
  min-width: 28px;
  text-align: center;
  padding: 0.25rem 0.375rem;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid $border-color;
  transition: all 0.15s ease-in-out;

  &.set-winner {
    color: $text-primary;
    font-weight: 600;
    background: rgba(59, 130, 246, 0.2);
    border-color: rgba(59, 130, 246, 0.4);
  }

  &.set-loser {
    color: $text-light;
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.08);
  }
}

@media (max-width: 768px) {
  .racquet-sports-container {
    padding: 0.75rem;
  }

  .match {
    padding: 0.75rem;
  }

  .player-name {
    font-size: 0.8rem;
  }

  .sets {
    font-size: 1rem;
    min-width: 18px;
    padding: 0.2rem 0.4rem;
  }

  .set-score {
    font-size: 0.75rem;
    min-width: 24px;
    padding: 0.2rem 0.3rem;
  }

  .scores {
    gap: 0.75rem;
  }

  .set-scores {
    gap: 0.25rem;
  }
}
</style>